import {
  MAP_WIDTH,
  MAP_HEIGHT,
  MAP_ROWS,
  MAP_TERRAIN_SPRITES,
  MAP_TREE_SPRITES,
  MAP_OBJECT_SPRITES,
  NPC_POSITIONS
} from '@/game/pico8MapData.js'

export { MAP_WIDTH, MAP_HEIGHT, MAP_ROWS }

export const TILE_SIZE = 8

/** 镜头较长边最多可见格数（地图格） */
export const MAX_VIEW_TILES = 25

/** 视野 / 小地图 AOI 半径（切比雪夫距离，与 backend AOI_RADIUS 同步） */
export const AOI_RADIUS = 13
export const MINIMAP_DIAMETER = AOI_RADIUS * 2 + 1

/** 同一格最多容纳的角色数（玩家 + NPC，与 backend MAX_PLAYERS_PER_CELL 同步） */
export const MAX_PLAYERS_PER_CELL = 3

/**
 * 镜头视窗：格数比例与容器一致 → 铺满画布、每格正方形；较长边 ≤ maxTiles。
 */
export function computeViewport (panelW, panelH, maxTiles = MAX_VIEW_TILES) {
  const pw = Math.max(1, Math.floor(panelW))
  const ph = Math.max(1, Math.floor(panelH))
  const cap = Math.max(4, Math.min(MAX_VIEW_TILES, maxTiles))
  const aspect = pw / ph

  let viewW
  let viewH

  if (pw >= ph) {
    viewW = Math.min(MAP_WIDTH, cap)
    viewH = Math.max(1, Math.min(MAP_HEIGHT, Math.round(viewW / aspect)))
  } else {
    viewH = Math.min(MAP_HEIGHT, cap)
    viewW = Math.max(1, Math.min(MAP_WIDTH, Math.round(viewH * aspect)))
  }

  const tilePx = pw / viewW
  const scale = tilePx / TILE_SIZE

  return {
    scale,
    viewW,
    viewH,
    tilePx,
    canvasW: pw,
    canvasH: ph
  }
}

/** 将总长 total 均分为 count 段，相邻无间隙、并铺满 [0, total) */
export function tileSpan (total, index, count) {
  const start = Math.round((index * total) / count)
  const end = Math.round(((index + 1) * total) / count)
  return { pos: start, size: end - start }
}

export function findTileIndex (coord, total, count) {
  if (coord < 0 || count < 1) return -1
  for (let i = 0; i < count; i++) {
    const { pos, size } = tileSpan(total, i, count)
    if (coord >= pos && coord < pos + size) return i
  }
  return -1
}

/** 逻辑格类型 — 与 backend/config/world_map.py 同步 */
export const TILE = {
  GRASS: 0,
  SIDEWALK: 1,
  ROAD: 2,
  BLOCKED: 3
}

export const NPCS = [
  {
    id: 'mcd',
    x: NPC_POSITIONS.mcd.x,
    y: NPC_POSITIONS.mcd.y,
    emoji: '🍟',
    label: '麦当当',
    minimapColor: '#ffb020',
    sprite: 'mcd'
  },
  {
    id: 'witch',
    x: NPC_POSITIONS.witch.x,
    y: NPC_POSITIONS.witch.y,
    emoji: '🔮',
    label: '塔罗女巫',
    minimapColor: '#b57cff',
    sprite: 'witch'
  },
  {
    id: 'stylist',
    x: NPC_POSITIONS.stylist.x,
    y: NPC_POSITIONS.stylist.y,
    emoji: '👗',
    label: '悠衣酷',
    minimapColor: '#ff6b9d',
    sprite: 'stylist'
  },
  {
    id: 'bobby',
    x: NPC_POSITIONS.bobby.x,
    y: NPC_POSITIONS.bobby.y,
    emoji: '📒',
    label: '波比',
    minimapColor: '#6ec4ff',
    sprite: 'bobby',
    actionLabel: '签到'
  },
  {
    id: 'lark',
    x: NPC_POSITIONS.lark.x,
    y: NPC_POSITIONS.lark.y,
    emoji: '🐦',
    label: '小百灵',
    minimapColor: '#9ccc65',
    sprite: 'lark',
    actionLabel: '音色'
  },
  {
    id: 'pixie',
    x: NPC_POSITIONS.pixie.x,
    y: NPC_POSITIONS.pixie.y,
    emoji: '🧚',
    label: '解析小精灵',
    minimapColor: '#4dd0e1',
    sprite: 'pixie',
    actionLabel: '解析'
  }
]

/** 主页预览访客默认出生点（麦当当东侧邻格） */
export const GUEST_SPAWN = {
  x: NPC_POSITIONS.mcd.x + 4,
  y: NPC_POSITIONS.mcd.y
}

/** 漫游海贼王（坐标由服务端 MAP_STATE 同步） */
export const PIRATE_NPC = {
  id: 'pirate',
  emoji: '🏴‍☠️',
  label: '海贼王',
  minimapColor: '#e74c3c',
  sprite: 'pirate',
  actionLabel: '开箱'
}

export const CASINO_ENTRANCE = { x: 8, y: 12, label: '酒馆' }

export function nearbyCasinoEntrance (px, py, radius = 1) {
  const d = chebyshev(px, py, CASINO_ENTRANCE.x, CASINO_ENTRANCE.y)
  return d <= radius ? CASINO_ENTRANCE : null
}

export const PROXIMITY_RADIUS = 6

export const TILE_COLORS = {
  [TILE.GRASS]: { base: '#5ec878', light: '#7ed890', dark: '#48a860' },
  [TILE.SIDEWALK]: { base: '#c8c0b0', light: '#ddd4c4', dark: '#a8a090' },
  [TILE.ROAD]: { base: '#5a5a62', light: '#707078', dark: '#404048' },
  [TILE.BLOCKED]: { base: '#8a5048', light: '#a06058', dark: '#603830' }
}

export const MINIMAP_TILE_COLORS = {
  [TILE.GRASS]: '#50b868',
  [TILE.SIDEWALK]: '#b0a898',
  [TILE.ROAD]: '#484850',
  [TILE.BLOCKED]: '#3d8fd4' // 水面（逻辑上 BLOCKED，小地图用蓝色）
}

/** 地图外 / 水面格绘制（与边缘 BLOCKED 格 terrain sprite 一致） */
export const WATER_TERRAIN_SPRITE = 39
export const WATER_FILL_COLOR = MINIMAP_TILE_COLORS[TILE.BLOCKED]

export function isOnMap (x, y) {
  const ix = Number(x)
  const iy = Number(y)
  return Number.isFinite(ix) && Number.isFinite(iy) && ix >= 0 && iy >= 0 && ix < MAP_WIDTH && iy < MAP_HEIGHT
}

export function tileAt (x, y) {
  const ix = Number(x)
  const iy = Number(y)
  if (!Number.isFinite(ix) || !Number.isFinite(iy)) return TILE.BLOCKED
  if (ix < 0 || iy < 0 || ix >= MAP_WIDTH || iy >= MAP_HEIGHT) return TILE.BLOCKED
  return MAP_ROWS[iy][ix]
}

export function terrainSpriteAt (x, y) {
  if (x < 0 || y < 0 || x >= MAP_WIDTH || y >= MAP_HEIGHT) return 0
  return MAP_TERRAIN_SPRITES[y][x]
}

export function objectSpriteAt (x, y) {
  if (x < 0 || y < 0 || x >= MAP_WIDTH || y >= MAP_HEIGHT) return -1
  return MAP_OBJECT_SPRITES[y][x]
}

/** Tiled Tree 图层 */
export function treeSpriteAt (x, y) {
  if (x < 0 || y < 0 || x >= MAP_WIDTH || y >= MAP_HEIGHT) return -1
  return MAP_TREE_SPRITES[y][x]
}

export function npcAt (x, y, extraNpcs = []) {
  for (const n of extraNpcs) {
    if (Number(n.x) === x && Number(n.y) === y) return n
  }
  return NPCS.find(n => n.x === x && n.y === y) || null
}

export function nearbyNpc (x, y, maxDist = 1, extraNpcs = []) {
  let best = null
  let bestDist = maxDist + 1
  const all = [...NPCS, ...extraNpcs]
  for (const npc of all) {
    const d = chebyshev(x, y, npc.x, npc.y)
    if (d <= maxDist && d < bestDist) {
      best = npc
      bestDist = d
    }
  }
  return best
}

export function nearbyChest (x, y, chests = [], maxDist = 1) {
  let best = null
  let bestDist = maxDist + 1
  for (const c of chests) {
    if (!Number.isFinite(c.x) || !Number.isFinite(c.y)) continue
    const d = chebyshev(x, y, c.x, c.y)
    if (d <= maxDist && d < bestDist) {
      best = c
      bestDist = d
    }
  }
  return best
}

export function chestAt (x, y, chests = []) {
  return chests.find(c => c.x === x && c.y === y) || null
}

/** 统计格子上 NPC 占位（含静态 NPC 与 extraNpcs，如海贼王） */
export function npcCountAt (x, y, extraNpcs = []) {
  let count = 0
  for (const npc of NPCS) {
    if (npc.x === x && npc.y === y) count++
  }
  for (const npc of extraNpcs) {
    if (Number(npc.x) === x && Number(npc.y) === y) count++
  }
  return count
}

export function cellOccupantCount (x, y, playerOccupancy = new Map(), extraNpcs = []) {
  return (playerOccupancy.get(`${x},${y}`) || 0) + npcCountAt(x, y, extraNpcs)
}

export function isWalkable (x, y, playerOccupancy = new Map(), extraNpcs = [], maxOccupants = MAX_PLAYERS_PER_CELL) {
  if (tileAt(x, y) === TILE.BLOCKED) return false
  if (cellOccupantCount(x, y, playerOccupancy, extraNpcs) >= maxOccupants) return false
  return true
}

export function chebyshev (x1, y1, x2, y2) {
  return Math.max(Math.abs(x1 - x2), Math.abs(y1 - y2))
}

export function isWithinAoi (x1, y1, x2, y2, radius = AOI_RADIUS) {
  return chebyshev(x1, y1, x2, y2) <= radius
}

export function getCamera (focusX, focusY, viewW, viewH) {
  const fx = Number.isFinite(Number(focusX)) ? Number(focusX) : 0
  const fy = Number.isFinite(Number(focusY)) ? Number(focusY) : 0
  const vw = Math.max(1, Math.min(MAP_WIDTH, Math.floor(Number(viewW)) || 1))
  const vh = Math.max(1, Math.min(MAP_HEIGHT, Math.floor(Number(viewH)) || 1))
  // 人物始终居中；地图外区域由渲染层以水域填充
  const cx = fx - (vw - 1) / 2
  const cy = fy - (vh - 1) / 2
  return { cx, cy, viewW: vw, viewH: vh }
}

export function findPath (fromX, fromY, toX, toY, playerOccupancy = new Map(), extraNpcs = []) {
  if (
    !Number.isFinite(fromX) ||
    !Number.isFinite(fromY) ||
    !Number.isFinite(toX) ||
    !Number.isFinite(toY)
  ) {
    return []
  }
  if (fromX === toX && fromY === toY) return []
  if (!isWalkable(toX, toY, playerOccupancy, extraNpcs)) return []

  const key = (x, y) => `${x},${y}`
  const queue = [[fromX, fromY]]
  const cameFrom = new Map()
  cameFrom.set(key(fromX, fromY), null)

  const dirs = [
    [-1, 0], [1, 0], [0, -1], [0, 1],
    [-1, -1], [-1, 1], [1, -1], [1, 1]
  ]

  while (queue.length) {
    const [cx, cy] = queue.shift()
    for (const [dx, dy] of dirs) {
      const nx = cx + dx
      const ny = cy + dy
      const k = key(nx, ny)
      if (cameFrom.has(k)) continue
      if (!isWalkable(nx, ny, playerOccupancy, extraNpcs)) continue
      cameFrom.set(k, [cx, cy])
      if (nx === toX && ny === toY) {
        const path = []
        let cur = [nx, ny]
        while (cur && (cur[0] !== fromX || cur[1] !== fromY)) {
          path.unshift(cur)
          cur = cameFrom.get(key(cur[0], cur[1]))
        }
        return path
      }
      queue.push([nx, ny])
    }
  }
  return []
}

export function randomLocalSpawn () {
  const candidates = []
  for (let y = 0; y < MAP_HEIGHT; y++) {
    for (let x = 0; x < MAP_WIDTH; x++) {
      if (isWalkable(x, y)) candidates.push([x, y])
    }
  }
  return candidates[Math.floor(Math.random() * candidates.length)]
}
